import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, ArrowLeft, Check } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function EmployeePage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    job_title: '',
    phone_number: '',
    username: '',
    password: '',
    date_of_joining: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [filters, setFilters] = useState({
    employee_id: '',
    name: '',
    phone_number: '',
  });

  useEffect(() => { loadEmployees(); }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployees();
      setData(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item =>
    (filters.employee_id === '' || item.employee_id?.toString().includes(filters.employee_id)) &&
    (filters.name === '' || item.name.toLowerCase().includes(filters.name.toLowerCase())) &&
    (filters.phone_number === '' || (item.phone_number || '').includes(filters.phone_number))
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  const emptyForm = {
    name: '',
    job_title: '',
    phone_number: '',
    username: '',
    password: '',
    date_of_joining: '',
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      job_title: item.job_title || '',
      phone_number: item.phone_number || '',
      username: item.username || '',
      password: '',
      date_of_joining: item.date_of_joining || '',
    });
    setCurrentView('form');
  };

  const handleDelete = async (employee_id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await apiService.deleteEmployee(employee_id);
        await loadEmployees();
        const newTotalPages = Math.ceil((filteredData.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) setCurrentPage(newTotalPages);
      } catch (error) {
        toast.error('Error deleting employee: ' + error.message);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.phone_number.trim()) {
      toast.warning('Name and phone number are required.');
      return;
    }
    if (!/^\d{10}$/.test(formData.phone_number)) {
      toast.warning('Please enter a valid 10-digit phone number.');
      return;
    }
    try {
      setLoading(true);
      if (editingItem) {
        await apiService.updateEmployee(editingItem.employee_id, formData);
      } else {
        await apiService.createEmployee(formData);
      }
      await loadEmployees();
      setCurrentView('list');
      setFormData(emptyForm);
      setEditingItem(null);
    } catch (error) {
      toast.error('Error saving employee: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setFormData(emptyForm);
    setEditingItem(null);
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return renderLayout(
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading employees...</p>
      </div>
    );
  }

  if (error && data.length === 0) {
    return renderLayout(
      <div className="error-container">
        <h3>Error loading employees</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadEmployees}>Retry</button>
      </div>
    );
  }

  // Form View
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
              {editingItem ? 'Edit Employee' : 'Add New Employee'}
            </h1>
          </div>
        </div>

        <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="form-group">
            <label htmlFor="name" className="form-label">Employee Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter employee name"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="job_title" className="form-label">Job Title</label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              className="form-input"
              value={formData.job_title}
              onChange={handleInputChange}
              placeholder="e.g. Manager, Supervisor, Helper"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone_number" className="form-label">Phone Number *</label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              className="form-input"
              value={formData.phone_number}
              onChange={handleInputChange}
              placeholder="Enter 10-digit phone number"
              maxLength="10"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="date_of_joining" className="form-label">Date of Joining</label>
            <input
              type="date" max={TODAY_DATE}
              id="date_of_joining"
              name="date_of_joining"
              className="form-input"
              value={formData.date_of_joining}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="username" className="form-label">Username (for login access)</label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Enter username"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password {editingItem ? '(leave blank to keep current)' : ''}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter password"
              disabled={loading}
            />
          </div>

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={18} />
              {loading ? 'Saving...' : (editingItem ? 'Update Employee' : 'Add Employee')}
            </button>
          </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">Employee Management</h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Employee
        </button>
      </div>

      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>ID</label>
          <input
            type="text"
            name="employee_id"
            className="form-input"
            value={filters.employee_id}
            onChange={handleFilterChange}
            placeholder="ID"
            style={{ width: '80px' }}
          />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Name</label>
          <input
            type="text"
            name="name"
            className="form-input"
            value={filters.name}
            onChange={handleFilterChange}
            placeholder="Search Name"
          />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Phone</label>
          <input
            type="text"
            name="phone_number"
            className="form-input"
            value={filters.phone_number}
            onChange={handleFilterChange}
            placeholder="Search Phone"
          />
        </div>
        <button className="btn btn-secondary" onClick={() => setFilters({ employee_id: '', name: '', phone_number: '' })} style={{ height: '42px', alignSelf: 'flex-end' }}>
          Clear
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Job Title</th>
              <th>Phone Number</th>
              <th>Date of Joining</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(item => (
              <tr key={item.employee_id}>
                <td>{item.employee_id}</td>
                <td style={{ fontWeight: 500 }}>{item.name}</td>
                <td>{item.job_title || '—'}</td>
                <td>{item.phone_number}</td>
                <td>{item.date_of_joining ? new Date(item.date_of_joining).toLocaleDateString() : '—'}</td>
                <td>
                  <div className="actions-cell">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(item)}
                      disabled={loading}
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      className="btn-icon"
                      style={{ color: 'var(--error-color)' }}
                      onClick={() => handleDelete(item.employee_id)}
                      disabled={loading}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {currentItems.length === 0 && !loading && (
          <div className="no-data">
            <p>No employees found matching your filters</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
            <ChevronLeft size={16} />
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button className="pagination-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {currentItems.length} of {filteredData.length} employees
      </div>
    </>
  );
}

export default EmployeePage;
